/**
 * Service Page
 * Programmatic SEO service landing pages
 */

import React from 'react';
import DentalVisitAnswers from '../components/DentalVisitAnswers';
import { visitFAQs } from '../data/visit';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs, { createBreadcrumbs } from '../components/Breadcrumbs';
import { RelatedLocations, RelatedPosts, ContextualCTA } from '../components/InternalLinks';
import Footer from '../components/Footer';
import BookAppointmentLink from '../components/BookAppointmentLink';
import { getServiceById, services, faqItems } from '../data/seo';
import {
  generateServiceSchema,
  generateMedicalProcedureSchema,
  generateFAQSchema,
  generateCanonicalUrl,
} from '../utils/seo';
import { PageMeta } from '../data/seo';

const ServicePage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const service = serviceId ? getServiceById(serviceId) : undefined;
  
  // 404 handling
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-brand-teal/10 rounded-full flex items-center justify-center">
            <i className="fas fa-tooth text-brand-teal text-4xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Service Not Found</h1>
          <p className="text-gray-600 mb-8 max-w-md">
            We couldn't find the service you're looking for. Check out our services below.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services" className="btn-primary px-6 py-3">
              View All Services
            </Link>
            <Link to="/" className="btn-secondary px-6 py-3">
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const meta: PageMeta = {
    title: `${service.name} | Pet Dental Care Fort Lauderdale | CHOMP`,
    description: `${service.longDescription} Professional ${service.name.toLowerCase()} for dogs and cats in South Florida. Starting at $${service.priceValue}.`,
    canonical: generateCanonicalUrl(`/services/${service.slug}`),
    keywords: service.keywords,
  };
  
  const breadcrumbs = createBreadcrumbs.service(service.name, service.slug);
  
  // Get related FAQs
  const relevantFAQs = service.id === 'routine-dental' ? visitFAQs : faqItems.filter(faq =>
    faq.category === 'General' || faq.category === 'Pricing' || faq.category === 'Process'
  ).slice(0, 4);
  
  // Get other services
  const otherServices = services.filter(s => s.slug !== service.slug);

  return (
    <>
      <SEO
        meta={meta}
        schema={[
          generateServiceSchema(service),
          generateMedicalProcedureSchema(service),
          generateFAQSchema(relevantFAQs),
        ]}
        breadcrumbs={breadcrumbs}
      />

      {/* Hero Section */}
      <section className="pt-8 pb-16 bg-gradient-to-br from-brand-teal/10 to-white">
        <div className="container-custom">
          <Breadcrumbs items={breadcrumbs} className="mb-6" />
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                {service.petTypes.map(pet => (
                  <span 
                    key={pet}
                    className="bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full text-sm font-semibold capitalize"
                  >
                    <i className={`fas fa-${pet} mr-1`}></i>
                    {pet}s
                  </span>
                ))}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6 font-nantes">
                {service.name}
              </h1>
              
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                {service.longDescription}
              </p>
              
              <div className="bg-white rounded-xl p-6 shadow-lg inline-block mb-8">
                <p className="text-sm text-gray-500 uppercase font-semibold mb-1">Starting at</p>
                <p className="text-3xl font-bold text-brand-teal">{service.price}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentLink 
                  className="inline-flex items-center justify-center bg-brand-teal text-white text-lg px-8 py-4 rounded-xl font-bold hover:bg-teal-700 transition-all shadow-lg"
                >
                  <i className="fas fa-calendar-alt mr-2"></i>
                  Book {service.name}
                </BookAppointmentLink>
                <a 
                  href="tel:+19544663775" 
                  className="inline-flex items-center justify-center gap-2 border-2 border-brand-teal text-brand-teal text-lg px-8 py-4 rounded-xl font-bold hover:bg-brand-teal hover:text-white transition-all"
                >
                  <i className="fas fa-phone"></i>
                  (954) 466-3775
                </a>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-brand-teal/20 to-brand-teal/5 rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="/Chomp-Front.png" 
                  alt={`${service.name} at CHOMP Pet Dental`}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-check text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Itemized Pricing</p>
                    <p className="text-sm text-gray-500">Estimate before treatment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {service.id === 'routine-dental' && <DentalVisitAnswers />}

      {/* What's Included */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center font-nantes">
            Your {service.name} Treatment Plan
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {service.features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg"
              >
                <div className="w-8 h-8 bg-brand-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check text-brand-teal text-sm"></i>
                </div>
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CHOMP */}
      <section className="bg-gray-50 py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-primary mb-12 text-center font-nantes">
            Why Choose CHOMP for {service.name}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-clock text-brand-teal text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Care Planned for Your Pet</h3>
              <p className="text-gray-600">
                Your veterinary team assesses your pet and plans the procedure, monitoring, and recovery care around their individual needs. Ask the team about risks and preparation.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-dollar-sign text-brand-teal text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Transparent Pricing</h3>
              <p className="text-gray-600">
                Review the published fees and ask for an itemized estimate. Bloodwork, extractions, and medications can affect the total; discuss how additional findings and treatment authorization will be handled.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-award text-brand-teal text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Dentistry-Focused Team</h3>
              <p className="text-gray-600">
                CHOMP focuses on dental care for dogs and cats. Meet our veterinarians and learn how we work alongside your pet’s primary-care practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center font-nantes">
            Other Pet Dental Services
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {otherServices.map((svc) => (
              <Link
                key={svc.slug}
                to={`/services/${svc.slug}`}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-teal/20 transition-colors">
                    <i className="fas fa-tooth text-brand-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 group-hover:text-brand-teal transition-colors mb-1">
                      {svc.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-2">{svc.shortDescription}</p>
                    <p className="text-brand-teal font-bold">{svc.price}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/services" className="text-brand-teal font-semibold hover:underline">
              View Full Services & Pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      {service.id !== 'routine-dental' && <section className="bg-gray-50 py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center font-nantes">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {relevantFAQs.map((faq, index) => (
              <details 
                key={index} 
                className="bg-white rounded-xl p-5 group shadow-sm"
                open={index === 0}
              >
                <summary className="font-bold text-gray-800 cursor-pointer list-none flex items-center justify-between">
                  {faq.question}
                  <i className="fas fa-chevron-down text-brand-teal transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed whitespace-pre-line">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>}

      {/* Locations */}
      <section className="bg-white py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6 font-nantes">
                {service.name} Serving South Florida
              </h2>
              <p className="text-gray-700 mb-6">
                We provide {service.name.toLowerCase()} for dogs and cats throughout South Florida, including Fort Lauderdale, Miami, Boca Raton, and surrounding areas.
              </p>
              <RelatedLocations limit={6} className="bg-gray-50" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6 font-nantes">
                Learn More About Pet Dental Care
              </h2>
              <RelatedPosts limit={3} />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          <ContextualCTA service={service} variant="box" />
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ServicePage;



